import { useRef } from 'react';
import type { PhaseProgressUpdate } from '@volley/crucible-ipc-types/ipc-channels';
import {
  classifyClientRelativeLiveness,
  initActivityLivenessMemo,
  initSnapshotPhaseStartMemo,
  latestActivityAt,
  livenessProvesLife,
  observeActivity,
  observeSnapshotPhaseStart,
  type ActivityLiveness,
  type ActivityLivenessMemo,
  type SnapshotPhaseStartMemo,
} from '../chat/activity-freshness';
import { useBuildEscalationClock } from '../workspace/use-build-escalation-clock';

export interface UseActivityLivenessArgs {
  /** The active game; a change resets the client-relative memo. */
  gameId: string | undefined;
  /**
   * Pod-stamped `lastActivityAt` from the polled interview-status response.
   * Combined with `phaseProgress.lastActivityAt` (they carry the same runtime
   * stamp; a given surface may only have one to hand).
   */
  interviewLastActivityAt?: number | null;
  /** Latest phase progress — the primary phase source AND a stamp carrier. */
  phaseProgress: PhaseProgressUpdate | null | undefined;
  /**
   * Foundry snapshot phase — the ALTERNATIVE phase source (round-5). The single
   * clock must arm off EITHER source or a snapshot-only build in the escalation
   * range leaves the clock frozen.
   */
  snapshotPhaseCurrent?: number | null;
  /**
   * The BUILD EPOCH id — the Foundry snapshot's `build.buildRunId`. A change
   * means a new build run, so proof-of-life from the prior run is discarded and
   * the new run starts in warm-up (#3033 round-3). Pass
   * `foundryGameState?.build.buildRunId ?? null`.
   */
  buildEpochKey?: string | null;
}

export interface UseActivityLivenessResult {
  /**
   * The single build-watch liveness verdict for DISPLAY, computed ONCE and
   * consumed by the status-row surfaces (chat row, right-panel now-summary) so
   * no consumer recomputes it against its own clock (#3034). Optimistic during
   * warm-up (reads `fresh` on the first-seen stamp). For the escalation gate use
   * `provenLive`, not this.
   */
  liveness: ActivityLiveness;
  /**
   * POSITIVE proof the build is alive RIGHT NOW — true only after a
   * client-OBSERVED advance AND while the verdict is fresh/quiet. This, NOT
   * `liveness`, gates the run-state escalation suppression: a warm-up `fresh`
   * (no observed advance yet) is NOT proof, so the 15-minute wall-clock and the
   * kernel idle-heartbeat escalations still fire on their own criteria and a
   * genuinely-dead build mounted-into is not masked (#3033 round-2).
   */
  provenLive: boolean;
  /**
   * The escalation clock reading. Fed to `deriveWorkspaceRunState` as
   * `signals.nowMs` for the 15-minute stale-BUILD wall-clock check
   * (`nowMs - phaseStart`) — a DIFFERENT signal from `lastActivityAt`, left
   * intact by #3033/#3034.
   */
  nowMs: number;
  /**
   * #3035 — CLIENT-clock time when the current Foundry snapshot `phase.current`
   * was first observed (per build epoch). The snapshot carries no phase-start
   * timestamp, so this is the phase-start anchor the run-state snapshot-only
   * stale-build detector ages against `nowMs`. `null` until a phase is observed.
   */
  snapshotPhaseStartedAt: number | null;
}

/**
 * The SINGLE build-watch liveness source (#3033 + #3034).
 *
 * Consolidates what used to be three independently-clocked derivations
 * (ChatPanel's `useElapsedTick`, WorkspaceRightPanel's `useElapsedTick`, and
 * run-state's `useBuildEscalationClock`-fed `deriveActivityLiveness`) into one
 * hook that:
 *   - keeps ONE per-game client-relative memo (`observeActivity`), so freshness
 *     is measured on the client clock only and client-vs-pod skew cancels
 *     (#3033);
 *   - ticks on ONE clock (`useBuildEscalationClock`) while the build is live in
 *     the escalation range — phase-source-agnostic, arming off the
 *     phase-progress subscription OR the Foundry snapshot phase (round-5) — so a
 *     stall still surfaces with no new poll data and no per-consumer interval to
 *     forget to advance (#3034);
 *   - classifies liveness ONCE from the client clock.
 *
 * The memo is advanced during render (a pure fold keyed by the observed stamp):
 * `observeActivity` is idempotent for the same inputs, so a strict-mode double
 * render records the same change-time. The escalation-clock tick re-renders the
 * host, re-reading `Date.now()` so the verdict re-derives while polls are quiet.
 */
export function useActivityLiveness(args: UseActivityLivenessArgs): UseActivityLivenessResult {
  const { gameId, interviewLastActivityAt, phaseProgress, snapshotPhaseCurrent, buildEpochKey } =
    args;

  // ONE clock: arms (phase-source-agnostic) and ticks while the build is live in
  // the escalation range; also supplies run-state's stale-build wall clock.
  const nowMs = useBuildEscalationClock(phaseProgress, snapshotPhaseCurrent);

  // Combine the two stamp carriers exactly as the retired per-consumer sites did.
  const lastActivityAt = latestActivityAt(
    interviewLastActivityAt,
    phaseProgress?.kind === 'derived' ? phaseProgress.lastActivityAt : null,
  );

  const memoRef = useRef<ActivityLivenessMemo>(initActivityLivenessMemo(gameId));
  // Read the client clock once for both the advance-fold and the classification
  // so they agree within the render. The clock tick above drives re-renders when
  // polls are quiet, so `Date.now()` keeps advancing the verdict during a stall.
  const clientNowMs = Date.now();
  memoRef.current = observeActivity(memoRef.current, {
    gameId,
    epochKey: buildEpochKey ?? null,
    lastActivityAt,
    clientNowMs,
  });
  const liveness = classifyClientRelativeLiveness(memoRef.current, clientNowMs);
  const provenLive = livenessProvesLife(memoRef.current, clientNowMs);

  // #3035 — the client-relative phase-start anchor for the snapshot-only
  // stale-build detector. Same epoch/game scoping and same client clock as the
  // liveness memo; a snapshot phase change re-anchors the 15-minute window.
  const phaseStartMemoRef = useRef<SnapshotPhaseStartMemo>(initSnapshotPhaseStartMemo(gameId));
  phaseStartMemoRef.current = observeSnapshotPhaseStart(phaseStartMemoRef.current, {
    gameId,
    epochKey: buildEpochKey ?? null,
    phaseCurrent: snapshotPhaseCurrent ?? null,
    clientNowMs,
  });
  const snapshotPhaseStartedAt = phaseStartMemoRef.current.clientTimeOfPhaseStart;

  return { liveness, provenLive, nowMs, snapshotPhaseStartedAt };
}
